import { addCopyToQuillHtml } from "@/constant/globalConstant";
import { getColor } from "@/utils/colorSchemas";
import PropTypes from "prop-types";
import React, { useState } from "react";
import * as FaIcons from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import TabModal from "./TabModal";
import { BiCopy } from "react-icons/bi";

function DashboardTabs({ mode = "view", data, setData, dashboard, setTabId, tabZoom = 1 }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabData = data?.tabs || [];
  const [showTabModal, setShowTabModal] = useState(false);
  const [editTabData, setEditTabData] = useState();

  const tabCopyHandler = (id) => {
    const selectedTab = tabData.find((i) => i.tabId === id || (i._id && i._id === id));
    const finalName = addCopyToQuillHtml(selectedTab?.name || "");
    const newTab = {
      ...selectedTab,
      tabId: uuid(),
      isSelected: false,
      name: finalName,
    };
    delete newTab?._id;
    if (newTab.liveCanvas) {
      newTab.liveCanvas = {
        ...newTab.liveCanvas,
        nodes: (newTab.liveCanvas.nodes || []).map((n) => {
          const rest = { ...n };
          delete rest._id;
          return { ...rest, data: { ...(rest.data || {}), _id: undefined } };
        }),
        edges: (newTab.liveCanvas.edges || []).map((e) => {
          const rest = { ...e };
          delete rest._id;
          return { ...rest, data: { ...(rest.data || {}), _id: undefined } };
        }),
      };
    }
    const nextTabs = [...(tabData || []), newTab];
    setData((pre) => ({ ...pre, hasTabs: true, tabs: nextTabs }));
  };

  const tabRemoveHandler = (tab) => {
    Swal.fire({
      title: "Delete Tab",
      text: "Do you wish to delete this tab?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (!result.isConfirmed) return;

      if (tabData.length === 1) {
        return Swal.fire({
          title: "At least one tab is required",
          icon: "warning",
          timer: 1500,
          confirmButtonText: "OK",
        });
      }
      const remaining = tabData.filter((i) => i.tabId !== tab.tabId && (!tab._id || i._id !== tab._id));

      // if removed tab was selected, select first remaining tab
      const nextTabs = tab.isSelected ? remaining?.map((t, idx) => ({ ...t, isSelected: idx === 0 })) : remaining;

      // ponytail: clean tabId from URL params when removing a tab
      if (searchParams.has("tabId")) {
        searchParams.delete("tabId");
        setSearchParams(searchParams, { replace: true });
      }

      setData((pre) => ({ ...pre, hasTabs: true, tabs: nextTabs }));
      if (tab.isSelected && nextTabs.length > 0 && setTabId) {
        setTabId(nextTabs[0]?._id || nextTabs[0]?.tabId || "");
      }
    });
  };

  const tabSelectHandler = (tab) => {
    // ponytail: clean tabId from URL params when manually switching tabs
    if (searchParams.has("tabId")) {
      searchParams.delete("tabId");
      setSearchParams(searchParams, { replace: true });
    }
    setData((pre) => {
      const nextTabs = pre?.tabs?.map((item) => ({
        ...item,
        isSelected: item.tabId === tab.tabId || (item._id && item._id === tab._id),
      }));
      return { ...pre, hasTabs: true, tabs: nextTabs };
    });
    setTabId(tab._id || tab.tabId);
  };

  const tabNumber = dashboard?.tabNumber || 4;

  return (
    <div className={`dashboard-tabs-wrapper ${tabNumber >= 7 ? "dashboard-tabs--dense" : ""}`}>

      <div className="dashboard-tabs-zoom-outer">
        <div
          className={`dashboard-tabs-zoom-inner ${tabZoom < 1 ? "dashboard-tabs--mobile-zoomed-out" : ""}`}
          style={{
            zoom: tabZoom,
            width: `calc(100% * ${tabZoom})`,
            minWidth: tabZoom > 1 ? `calc(100% * ${tabZoom})` : undefined,
          }}
        >
          <div
            className={`dashboard-tabs-grid dashboard-tabs--cols-${tabNumber} ${tabZoom < 1 ? "dashboard-tabs--mobile-2col" : ""} pt-1 pt-md-2`}
            style={{ "--tabs-in-row": tabNumber }}
          >
            {/* prev tabs */}
            {tabData?.map((tab) => (
              <div key={tab.tabId || tab._id} className="dashboard-tab-col">
                <div
                  className={`dashboardTab ${tab?.isSelected ? "active" : ""}`}
                  style={{
                    "--a": getColor(tab?.colorSchemeName, 0),
                    "--b": getColor(tab?.colorSchemeName, 1),
                    "--c": getColor(tab?.colorSchemeName, 2),
                  }}
                  onClick={() => tabSelectHandler(tab)}
                >
                  {mode !== "view" && (
                    <div className="editItems">
                      <div onClick={() => tabCopyHandler(tab.tabId)}>
                        <BiCopy className="me-2" />
                      </div>
                      <div
                        onClick={() => {
                          setEditTabData(tab);
                          setShowTabModal(true);
                        }}
                      >
                        <FiEdit className="me-1" />
                      </div>
                      {tabData.length > 1 && (
                        <div onClick={() => tabRemoveHandler(tab)}>
                          <MdDeleteForever className="fs-5" />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="ql">
                    <div className="ql-editor p-0 pb-1 pe-2">
                      <div dangerouslySetInnerHTML={{ __html: tab?.name }}></div>
                    </div>
                  </div>
                  <div className="tabIcon mt-auto"> {tab?.icon ? React.createElement(FaIcons[tab?.icon]) : ""} </div>
                </div>
              </div>
            ))}
            {mode !== "view" && (
              <div className="dashboard-tab-col">
                <div onClick={() => setShowTabModal((p) => !p)} className="dashboardTab addTab">
                  +
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <TabModal
        {...{
          showTabModal,
          setShowTabModal,
          tabData,
          setData,
          editTabData,
          setEditTabData,
          setTabId,
          dashboard,
          data,
        }}
      />
    </div>
  );
}

export default DashboardTabs;

DashboardTabs.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.object,
  setData: PropTypes.func,
  dashboard: PropTypes.object,
  setTabId: PropTypes.func,
  tabZoom: PropTypes.number,
};
